import React from 'react'
import { Button, Input, InfoBox, CustomerServiceTelephone } from '@mobi/component-library/Common/V2'
import { DropdownInput } from '@mobi/component-library/Selection&Input'
import { DepositLimitFrequency } from '@mobi/api-types'
import { Spinner } from '@mobi/component-library/Common/Spinner'
import { Content, Footer, SignUpPageStyled } from './SignUpPage.styled'
import { AccountHeader } from '@mobi/component-library/Account'
import { getSignUpState } from '@mobi/account/Areas/SignUp/Store'
import {
  validateDepositLimitAmount,
  DEPOSIT_LIMIT_MIN,
  DEPOSIT_LIMIT_MAX,
  NUMERIC_REGEX,
} from '@mobi/account/Areas/SignUp/Utils/FormValidation'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { put, queryKeys, SignUpUrl } from '@mobi/account/Utils'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router'
import { routes } from '../Utils'
import { initializeWithCountIfNotInitialized } from '@mobi/account/Areas/Deposit/Utils'
import { trackSignUpNoLimitSetConfirmed, trackSignUpSetDepositLimits } from '../Utils/ga'
import { addToast } from '@mobi/component-library/Feedback/Toast'
import { Modal } from '@mobi/component-library/Overlays/Modal'
import { colors, font, spacing } from '@mobi/component-library/Theme/Common'
import styled from '@emotion/styled'

const depositLimitFrequencies: Exclude<DepositLimitFrequency, null>[] = [
  'Daily',
  'Weekly',
  'Fortnightly',
  'Monthly',
]

type TypeDepositLimitAndAnniversaryRequest = {
  optInIndicator: boolean
  optInMarketingIndicator: boolean
  preCommitmentMaxDepositAmount?: number
  preCommitmentMaxDepositFrequency?: string
}

export const DepositLimit = () => {
  const [amount, setAmount] = React.useState('')
  const [frequency, setFrequency] = React.useState<DepositLimitFrequency>(null)
  const [amountValidationMessage, setAmountValidationMessage] = React.useState('')
  const [isConfirmModalOpen, setIsConfirmModalOpen] = React.useState(false)
  const [hasShownConfirmModal, setHasShownConfirmModal] = React.useState(false)
  const signUpState = useSelector(getSignUpState)
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const isFrequencySelected = !!frequency
  const amountNumber = Number(amount)

  const saveDepositLimit = useMutation({
    mutationFn: async () => {
      const requestBody: TypeDepositLimitAndAnniversaryRequest = {
        optInIndicator: isFrequencySelected,
        optInMarketingIndicator: signUpState?.signupApplication?.marketingOptInIndicator ?? false,
      }

      if (isFrequencySelected) {
        requestBody.preCommitmentMaxDepositAmount = amountNumber
        requestBody.preCommitmentMaxDepositFrequency = frequency
      }

      if (signUpState?.newUserBetAccount) {
        return await put<TypeDepositLimitAndAnniversaryRequest, void>(
          SignUpUrl.saveDepositLimitAndAnniversary(signUpState?.newUserBetAccount),
          requestBody
        )
      }
    },
    mutationKey: [queryKeys.signupPrecommitment],
    onSuccess: () => {
      trackSignUpSetDepositLimits(frequency, amount)
      addToast({
        id: 'deposit-limit-success',
        isDismissable: true,
        title: 'Your deposit limit has been set.',
        icon: 'success',
      })
      queryClient.removeQueries({ queryKey: queryKeys.depositInitialData })
      document.getElementById('deposit-limit-anniversary-popup')?.remove()
      if (signUpState?.newUserBetAccount) {
        initializeWithCountIfNotInitialized(signUpState?.newUserBetAccount, 0)
      }
      navigate(routes.signUpComplete)
    },
  })

  const onSubmit = () => {
    // Only show the confirm modal once
    if (!isFrequencySelected && !hasShownConfirmModal) {
      setIsConfirmModalOpen(true)
      setHasShownConfirmModal(true)
      return
    }

    saveDepositLimit.mutate()
  }

  const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value
    const sanitizedInputValue = inputValue.replace(NUMERIC_REGEX, '')
    setAmount(sanitizedInputValue)
    setAmountValidationMessage(validateDepositLimitAmount(sanitizedInputValue))
  }

  const applicationId = signUpState?.signupVerification?.applicationReferenceNumber?.toString()

  const ErrorInfoBox = () => {
    return (
      <InfoBox color='red' size='md'>
        <b>Sorry, an error occurred!</b>
        <div>
          There was an error in setting your deposit limit. Please try again, or contact us on{' '}
          <CustomerServiceTelephone />{' '}
          {!!applicationId && 'and provide the following Application ID '}
          so we can assist you in completing your registration.
          <br />
          <br />
          {!!applicationId && <span>Application ID: {applicationId}</span>}
        </div>
      </InfoBox>
    )
  }

  if (signUpState?.isNewUserAutoLoggedIn === false || !signUpState?.newUserBetAccount) {
    navigate(routes.home)
  }

  return (
    <SignUpPageStyled>
      {saveDepositLimit.isPending && <Spinner />}
      {saveDepositLimit.isError && <ErrorInfoBox />}
      <AccountHeader
        alignItems='start'
        title={`You're almost finished, ${signUpState?.signupApplication?.firstName}`}
        description='Would you like to set a deposit limit? Your deposit limit can also be set later.'
        style={{ marginBottom: '0rem' }}
      />
      <Content>
        <DropdownInput
          label='Select deposit frequency'
          name='sign-up-deposit-frequency'
          value={frequency || undefined}
          onChange={event => {
            setFrequency((event.target.value || null) as DepositLimitFrequency)
          }}
        >
          <option value=''>No Personal Limit</option>
          {depositLimitFrequencies.map(freq => (
            <option key={freq} value={freq}>
              {freq}
            </option>
          ))}
        </DropdownInput>
        <Input
          label='Enter limit amount'
          name='sign-up-deposit-limit-amount'
          helpText={
            amountValidationMessage
              ? amountValidationMessage
              : 'Any future increases to your deposit limit will include a 7 day cooling off period.'
          }
          prefix={amount.length > 0 && '$'}
          type='text'
          min={DEPOSIT_LIMIT_MIN}
          max={DEPOSIT_LIMIT_MAX}
          maxLength={DEPOSIT_LIMIT_MAX.toFixed(0).length}
          value={isFrequencySelected ? amount : ''}
          onChange={handleAmountChange}
          isInvalid={isFrequencySelected && !!amountValidationMessage}
          isDisabled={!isFrequencySelected}
          placeholder={isFrequencySelected ? 'Please enter an amount' : 'N/A'}
          inputMode='numeric'
        />
      </Content>
      <Footer>
        <Button
          color='primary'
          isFullWidth
          size='md'
          disabled={
            isFrequencySelected &&
            (isNaN(amountNumber) ||
              amountNumber < DEPOSIT_LIMIT_MIN ||
              amountNumber > DEPOSIT_LIMIT_MAX ||
              !Number.isInteger(amountNumber))
          }
          onClick={onSubmit}
        >
          Set deposit limit
        </Button>
      </Footer>

      <Modal
        headerIcon='confirm'
        headerIconAlignment='left'
        headerTitle='Are you sure?'
        headerSubtitle='Deposit limits can help you proactively control your online wagering by:'
        headerTextAlignment='left'
        isOpen={isConfirmModalOpen}
        dismissStatus='disallowed'
        footerComponent={
          <ModalFooterStyled>
            <Button
              isFullWidth
              color='primary'
              onClick={() => {
                trackSignUpNoLimitSetConfirmed()
                onSubmit()
                setIsConfirmModalOpen(false)
              }}
            >
              Continue without deposit limit
            </Button>

            <Button
              isFullWidth
              color='secondary_color'
              onClick={() => setIsConfirmModalOpen(false)}
            >
              Set deposit limit
            </Button>
          </ModalFooterStyled>
        }
      >
        <ConfirmModalContent>
          <ol>
            <li>Assisting with tracking your expenditure</li>
            <li>Removing the temptation to spend more and chase losses</li>
            <li>Limiting your daily wagering</li>
          </ol>
        </ConfirmModalContent>
      </Modal>
    </SignUpPageStyled>
  )
}

// ======
// Styles
// ======

const ModalFooterStyled = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: spacing.smx1,
  width: '100%',
})

const ConfirmModalContent = styled.div({
  color: colors.neutral[800],

  '> ol': {
    textAlign: 'left',
    marginBottom: spacing.bigx1,
    marginTop: spacing.none,
    paddingLeft: font.size.lg.lineHeight,

    '> li': {
      ...font.size.lg,
      lineHeight: font.size.xl.lineHeight,
    },
  },
})
